import React from "react";
import { motion } from "framer-motion";
import {
  Droplets,
  Cloud,
  Leaf,
  Droplet,
  Hand,
  Waves,
  Bath,
  Heart,
  Flame,
} from "lucide-react";

const treatments = [
  {
    icon: Droplets,
    title: "Abhyangam",
    detail:
      "Full-body warm oil massage that restores strength and calms the nervous system.",
  },
  {
    icon: Cloud,
    title: "Steam Bath",
    detail:
      "Herbal steam that opens channels, eases stiffness and aids deep relaxation.",
  },
  {
    icon: Leaf,
    title: "Kizhi",
    detail:
      "Warm herbal poultices pressed over the body: Ela Kizhi with fresh medicinal leaves to relieve aches, or Njavara Kizhi with medicated rice cooked in herbal milk for deep tissue nourishment. The doctor chooses what suits your recovery.",
  },
  {
    icon: Droplet,
    title: "Pichu",
    detail:
      "Warm medicated oil held over specific areas with soft cloth, for focused healing.",
  },
  {
    icon: Hand,
    title: "Udhwarthanam",
    detail:
      "Herbal powder massage that tones the body and supports healthy circulation.",
  },
  {
    icon: Waves,
    title: "Dhara",
    detail:
      "A continuous warm stream poured over the body: Kashaya Dhara with herbal decoction for strength and healing, or Thakra Dhara with medicated buttermilk for calm sleep and a settled mind.",
  },
  {
    icon: Bath,
    title: "Vethu Kuli (Herbal Bath)",
    detail:
      "Bathing water boiled with medicinal leaves, a daily ritual of Prasava Raksha.",
  },
  {
    icon: Heart,
    title: "Abdomen Bandaging",
    detail:
      "Traditional belly binding that supports the womb and helps the abdomen regain shape.",
  },
  {
    icon: Flame,
    title: "Dhoopanam (Hair Fumigation)",
    detail:
      "Gentle herbal smoke for the hair, keeping it dry, fragrant and healthy.",
  },
];

export const Treatments = () => {
  return (
    <section
      id="treatments"
      className="py-16 md:py-36 bg-ivory"
      data-testid="treatments-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            The Treatments
          </div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
            Classical therapies, <span className="italic text-sage-700">chosen for you</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
            These are the Ayurvedic postnatal treatments we practise at
            Kakkanat, Kerala. Each is prepared in-house, each performed by
            experienced hands.
          </p>
        </motion.div>

        <div className="mt-10 lg:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {treatments.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group h-full flex flex-col p-7 md:p-8 rounded-3xl bg-white border border-sage/15 hover:border-sage/40 transition-colors"
                data-testid={`treatment-card-${i + 1}`}
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-sage/10 text-sage transition-colors group-hover:bg-sage group-hover:text-ivory">
                  <Icon size={19} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-serif text-xl md:text-2xl text-charcoal">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-charcoal/65 leading-relaxed">
                  {t.detail}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 md:mt-14 p-8 md:p-10 rounded-3xl border border-sage/25 bg-sand/50 bg-grain"
          data-testid="treatments-note"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            How your plan is made
          </div>
          <h3 className="mt-3 font-serif text-2xl md:text-3xl text-charcoal">
            No fixed templates.
          </h3>
          <p className="mt-3 text-sm md:text-base text-charcoal/70 leading-relaxed max-w-3xl">
            We don't follow a standard treatment chart. Your plan is curated
            only after our doctor consults with you, based on your health
            condition, type of delivery, strength, and the package you choose.
            Some therapies are included; some are best avoided in your case.
            That's why everything at Kakkanat is personalised.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Treatments;
